import {
    makePopoverHandler,
    isInFixedLayer,
    appendModalControllerIfRequired,
    runInNextFrame,
} from './helpers.js';
import { eventBus } from './event-bus.js';
import type {
    UIModalAlignment,
    UIModalType,
} from '../components/modal/ui-modal.types.js';
import type { UIModalConfig } from '../components/modal/ui-modal-controller.types.js';
import type {
    InvertiblePopoverOptions,
    MenuPopoverConfig,
    MenuPopoverController,
    MenuPopoverPositionHandler,
} from './menu-popover.type.js';
import { browser } from './browser';

/**
 * @memberof SharedComponents
 * @class MenuPopover
 * @classdesc
 * Helpers for opening a ui-modal popover, keeping the imperative glue
 * (open/close, outside click, positioning) in one testable place.
 * Layer switching inside the content is handled by ui-slideview.
 */

/**
 * @callback MenuPopoverPositionHandler
 * @returns {UIModalPositionResult}
 */

const MENU_TYPE: UIModalType = 'menu';
const MENU_HORIZONTAL_PADDING = 48;
const DESKTOP_MODAL_WIDTH = 480;
const VERTICAL_OFFSET = 8;
const HORIZONTAL_OFFSET = 0;
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

/**
 * Builds a popover position handler that right aligns the popover to the trigger
 * by default and left aligns it as a fallback when right-aligning would overflow
 * the viewport's edge.
 *
 * The vertical axis (drops below the trigger) plus scroll and
 * fixed-layer handling come from makePopoverHandler; only the horizontal rule
 * (which makePopoverHandler cannot express) is derived here.
 * @memberof Helpers
 * @param {HTMLElement} trigger
 * @param {HTMLElement} content
 * @param {InvertiblePopoverOptions} [options]
 * @returns {MenuPopoverPositionHandler}
 */
const makeInvertiblePopoverHandler = (
    trigger: HTMLElement,
    content: HTMLElement,
    options: InvertiblePopoverOptions = {}
): MenuPopoverPositionHandler => {
    const verticalOffset = options.verticalOffset ?? VERTICAL_OFFSET;
    const horizontalOffset = options.horizontalOffset ?? HORIZONTAL_OFFSET;

    const getModalWindow = (): HTMLElement | null =>
        content.closest('.ui-modal__window');
    const getModalWidth = (): number => {
        const modalWindow = getModalWindow();
        return browser.run(
            () => (modalWindow ? modalWindow.offsetWidth : content.offsetWidth),
            0
        );
    };
    const getModalHeight = (): number => {
        const modalWindow = getModalWindow();
        return browser.run(
            () =>
                modalWindow ? modalWindow.offsetHeight : content.offsetHeight,
            0
        );
    };

    const verticalHandler = makePopoverHandler(trigger, {
        popoverWidth: getModalWidth,
        popoverHeight: getModalHeight,
        verticalOffset,
        horizontalOffset,
        supportTopAlignment: false,
        supportMiddleAlignment: false,
        invertHorizontalAlignment: true,
    });

    return () => {
        const result = verticalHandler();
        const rect = trigger.getBoundingClientRect();
        const doc = document.documentElement;
        // A trigger inside a fixed layer is positioned in viewport space so the
        // scroll offset must not be added or x drifts as the page scrolls.
        const scrollLeft = isInFixedLayer(trigger)
            ? 0
            : doc.scrollLeft || document.body.scrollLeft;
        const width = getModalWidth();

        // Default: right-align the popover to the trigger.
        let xStart = rect.right + scrollLeft - width - horizontalOffset;
        let shift = 'none';

        // Fallback: right aligning would overflow the edge so left align to
        // the trigger while keeping the popover inside the right edge.
        if (xStart < scrollLeft) {
            xStart = rect.left + scrollLeft + horizontalOffset;
            shift = 'left';
            const maxXStart = browser.run(
                () => scrollLeft + window.innerWidth - width,
                scrollLeft - width
            );
            if (xStart > maxXStart) {
                xStart = Math.max(scrollLeft, maxXStart);
            }
        }

        return {
            x: `${xStart}px`,
            y: result.y,
            align: result.align as UIModalAlignment,
            shift,
        };
    };
};

/**
 * Wires a trigger and its popover content into a ui-modal menu.
 * For layered content inside the menu, compose with ui-slideview.
 * @memberof Helpers
 * @param {MenuPopoverConfig} config
 * @returns {MenuPopoverController}
 */
const makeMenuPopover = (config: MenuPopoverConfig): MenuPopoverController => {
    const { trigger, content } = config;
    const type = config.type ?? MENU_TYPE;
    const mobileMediaQuery = config.mobileMediaQuery ?? MOBILE_MEDIA_QUERY;
    const desktopWidth =
        config.width ?? DESKTOP_MODAL_WIDTH - MENU_HORIZONTAL_PADDING;

    let modalElement: HTMLElement | null = null;
    let outsideClickRemover: (() => void) | null = null;
    let windowBlurRemover: (() => void) | null = null;

    const isOpen = (): boolean => modalElement !== null;
    const isMobile = (): boolean =>
        browser.run(() => window.matchMedia(mobileMediaQuery).matches, false);

    const clearOutsideListeners = (): void => {
        outsideClickRemover?.();
        windowBlurRemover?.();
    };

    const setupOutsideListeners = (): void => {
        const clickHandler = (event: MouseEvent): void => {
            const target = event.target as Node;
            if (
                modalElement &&
                !modalElement.contains(target) &&
                !trigger.contains(target)
            ) {
                close();
            }
        };
        const blurHandler = (): void => close();

        // Deferred so the click that opened the menu does not immediately close it.
        runInNextFrame(() => {
            document.addEventListener('click', clickHandler, true);
            outsideClickRemover = () => {
                document.removeEventListener('click', clickHandler, true);
                outsideClickRemover = null;
            };

            window.addEventListener('blur', blurHandler);
            windowBlurRemover = () => {
                window.removeEventListener('blur', blurHandler);
                windowBlurRemover = null;
            };
        });
    };

    const handleOpen = (): void => {
        modalElement = content.closest('ui-modal');
        clearOutsideListeners();
        setupOutsideListeners();
        trigger.setAttribute('aria-expanded', 'true');
        config.onOpen?.();
    };

    const handleClose = (): void => {
        clearOutsideListeners();
        content.style.width = '';
        content.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        modalElement = null;
        config.onClose?.();
    };

    const open = (): void => {
        if (isOpen()) {
            return;
        }

        // Mobile opens as a full-width bottom sheet so we leave the width unset
        content.style.width = isMobile() ? '' : `${desktopWidth}px`;
        content.hidden = false;

        appendModalControllerIfRequired();

        const modalConfig: UIModalConfig = {
            type,
            content,
            params: {
                target: trigger,
                attributes: { closable: true, ...config.attributes },
                position:
                    config.position ??
                    makeInvertiblePopoverHandler(trigger, content),
                onOpen: handleOpen,
                onClose: handleClose,
            },
        };

        eventBus.dispatchCustomEvent('modal-open', modalConfig);
    };

    const close = (): void => {
        const modal = modalElement ?? content.closest('ui-modal');
        modal?.dispatchEvent(new CustomEvent('modal-close', { bubbles: true }));
    };

    const toggle = (): void => (isOpen() ? close() : open());

    const destroy = (): void => {
        clearOutsideListeners();
    };

    return {
        open,
        close,
        toggle,
        destroy,
        get isOpen(): boolean {
            return isOpen();
        },
    };
};

export { makeInvertiblePopoverHandler, makeMenuPopover };
